"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import useWindowSize from "apps/website/hooks/useWindowSize";
import cx from "apps/website/utils/misc/cx";
import { useAPI } from "apps/website/hooks/useAPI";
import { FieldValue } from "@forms/schema";
import {
  PostCheckoutSurveyAnswers,
} from "libs/types/survey-answers/src/dto/PostCheckoutSurveyAnswer.dto";
import { scrollToContent } from "apps/website/utils/website/scrollToContent";
import { shuffleArray } from "libs/utils/misc/src/arrays";

import Modal from "../Modal/Modal";
import RadioGroup from "../../form/RadioButtons/RadioGroup/RadioGroup";
import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import { CTAButton } from "../../base/CTAButton/CTAButton";
import { InputField } from "../../form/Input/v2/InputField";

import { OTHER_OPTION, POST_CHECKOUT_SURVEY_OPTIONS } from "./constants";

const ANIMATION_DURATION = 800;
const TIME_UNTIL_OPEN = 1000;

export const PostCheckoutSurvey = () => {
  const t = useTranslations("Feature.PostCheckoutSurvey");
  const tUX = useTranslations("General.UX");

  const [ isOpen, setIsOpen ] = useState(false);
  const [ isOpening, setIsOpening ] = useState(false);
  const [ selectedOption, setSelectedOption ] = useState<string | null>(null);
  const [ otherAnswer, setOtherAnswer ] = useState<string | null>(null);
  const [ randomisedOptions, setRandomisedOptions ] = useState([ ...POST_CHECKOUT_SURVEY_OPTIONS, OTHER_OPTION ]);

  const { isActiveBreakpointBelow } = useWindowSize();
  const isMobile = isActiveBreakpointBelow("md");

  const { SurveyAnswers } = useAPI();

  useEffect(() => {
    setRandomisedOptions([ ...shuffleArray(POST_CHECKOUT_SURVEY_OPTIONS), OTHER_OPTION ]);
  }, []);

  useEffect(() => {
    if (localStorage.getItem("has-seen-post-checkout-survey") === "true") {
      return;
    }

    // We don't want to open the survey immediately, so we wait for 1 second.
    const timeout = setTimeout(() => {
      setIsOpen(true);
      setIsOpening(true);
      localStorage.setItem("has-seen-post-checkout-survey", "true");
    }, TIME_UNTIL_OPEN);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleClose = () => {
    setIsOpening(false);
    // Wait for the animation to complete before actually closing the modal
    const timeout = setTimeout(() => {
      setIsOpen(false);
    }, ANIMATION_DURATION); // Match the animation duration

    return () => {
      clearTimeout(timeout);
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOption) {
      handleClose();
      return;
    }

    const payload: PostCheckoutSurveyAnswers = {
      values: {
        "where-did-you-first-hear-about-katkin": {
          label: "Where did you first hear about KatKin?",
          value: selectedOption as FieldValue,
        },
        ...(selectedOption === "other" && {
          "other-answer": {
            label: "Tell us more",
            value: otherAnswer as FieldValue,
          },
        }),
      },
    };

    try {
      await SurveyAnswers.storePostCheckoutSurveyAnswers(payload);
    } catch (error) {

      console.error("An error occurred when storing answers to the post checkout survey", error);
    }

    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      id="post-checkout-survey"
      component={PostCheckoutSurvey.name}
      modalTitleId="post-checkout-survey-title"
      modalDescriptionId="post-checkout-survey-description"
      type={isMobile ? "slideUp" : "default"}
      theme="light-grey"
      hideCloseButton={true}
      disableClickOutside={true}
      size={isMobile ? "full" : "default"}
      gap="none"
      padding="none"
      customChild={true}
    >
      <form onSubmit={handleSubmit} className={cx(
        "flex flex-col max-h-[70vh] md:max-h-[75vh] relative bg-light-grey rounded-xl md:rounded-t-xl w-full md:w-[90%] md:max-w-[1200px] overflow-y-auto overflow-x-clip",
        isMobile && "duration-[800ms] ease-in-out transition-all before:transform before:-translate-x-1/2 before:top-0 before:left-1/2 before:w-screen before:h-full",
        isMobile && isOpening && "opacity-100 translate-y-0",
        isMobile && !isOpening && "opacity-0 translate-y-full",
      )}>
        <div className="pt-4 pb-6 px-5 bg-light-grey flex-shrink-0">
          <TextSubtitle size={1} align="center" >{ t("title") }</TextSubtitle>
        </div>
        <div className="flex-1 px-5 overscroll-contain w-full pb-4">
          <div className="w-full md:max-w-[358px]  mx-auto">
            <RadioGroup
              name="where-did-you-first-hear-about-katkin"
              options={randomisedOptions.map((option) => ({
                ...option,
                label: t(`options.${option.value}`),
              }))}
              onChange={(value) => {
                setSelectedOption(value);
                requestAnimationFrame(() => {
                  scrollToContent("post-checkout-survey-submit", { behavior: "smooth", block: "end" });
                });
              }}
              legend={t("title")}
              srOnlyLegend={true}
              selectedValue={selectedOption ?? undefined}
            />
            <div className="w-full mt-4">
              { selectedOption === "other" && (
                <InputField
                  name="post-checkout-survey-other"
                  srLabel={t("other:label")}
                  placeholder={t("other:placeholder")}
                  size="full"
                  onChange={(e) => setOtherAnswer(e.target.value)}
                  value={otherAnswer ?? undefined} />
              ) }
            </div>
          </div>
        </div>
        <div className="bg-white p-4 flex justify-center md:pb-4 pb-[100px]" id="post-checkout-survey-submit">
          <div className="w-full md:max-w-[358px] mx-auto" >
            <CTAButton text={tUX("continue").toUpperCase()} type="submit" size="full-width" />
          </div>
        </div>
      </form>
    </Modal>
  );
};
